import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { toast } from 'sonner';
import { GripVertical, Pencil, Trash2, ArrowRightLeft, ArrowUpToLine } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SortableVideoRow = ({ video, onEdit, onDelete, onMove, onSendToTop }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: video.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className="bg-muted/30" data-testid={`library-video-row-${video.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Drag handle */}
          <button
            type="button"
            className="mt-1 cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
            data-testid={`library-video-drag-${video.id}`}
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-5 h-5" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge variant="outline" className="text-xs capitalize">{video.category?.replace('_', ' ')}</Badge>
              <Badge
                variant="outline"
                className={`text-xs capitalize ${
                  video.difficulty === 'beginner' ? 'bg-green-500/10 border-green-500/30 text-green-700' :
                  video.difficulty === 'intermediate' ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-700' :
                  'bg-red-500/10 border-red-500/30 text-red-700'
                }`}
              >
                {video.difficulty}
              </Badge>
              {video.duration && (
                <span className="text-xs text-muted-foreground">{video.duration}</span>
              )}
            </div>
            <h4 className="font-semibold truncate">{video.title}</h4>
            <p className="text-sm text-muted-foreground line-clamp-2">{video.description}</p>
            {(!video.category || video.category === 'general' || !video.difficulty || video.difficulty === 'advanced') && (
              <p className="text-xs text-amber-700 font-medium mt-1" data-testid={`library-video-needs-review-${video.id}`}>
                ⚠ Needs review — {(!video.category || video.category === 'general') && 'no category'}
                {((!video.category || video.category === 'general') && (!video.difficulty || video.difficulty === 'advanced')) && ' + '}
                {(!video.difficulty || video.difficulty === 'advanced') && 'no difficulty'}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground/70 mt-1 font-mono" data-testid={`library-video-created-at-${video.id}`}>
              created_at: {video.created_at ? String(video.created_at) : <span className="text-red-500 font-semibold">MISSING</span>}
            </p>
            {video.tags?.length > 0 && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {video.tags.map((tag, i) => (
                  <span key={i} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {video.thumbnail_url && (
            <img
              src={video.thumbnail_url}
              alt=""
              className="w-24 h-16 object-cover rounded border border-border hidden sm:block"
              style={video.thumbnail_position ? { objectPosition: video.thumbnail_position } : undefined}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}

          <div className="flex gap-1">
            {onSendToTop && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSendToTop(video)}
                className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700"
                data-testid={`library-video-send-top-${video.id}`}
                title="Send to top of subscriber list"
              >
                <ArrowUpToLine className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(video)}
              className="h-8 w-8 p-0"
              data-testid={`library-video-edit-${video.id}`}
              title="Edit"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            {onMove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMove(video)}
                className="h-8 w-8 p-0 text-primary hover:text-primary"
                data-testid={`library-video-move-${video.id}`}
                title="Move to event"
              >
                <ArrowRightLeft className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(video.id)}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              data-testid={`library-video-delete-${video.id}`}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const LibraryVideosSortable = ({ videos, onEdit, onDelete, onMove, onSendToTop, onReordered }) => {
  const [items, setItems] = useState(videos);
  const [saving, setSaving] = useState(false);

  // Keep local state in sync when parent changes the list (e.g., after add/delete)
  useEffect(() => {
    setItems(videos);
  }, [videos]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    // Persist new order to backend
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/library/reorder`,
        { ordered_ids: newItems.map((i) => i.id) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('New order saved');
      if (onReordered) onReordered(newItems);
    } catch (err) {
      toast.error('Failed to save order — reverted');
      setItems(items); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Drag the <GripVertical className="inline w-3 h-3" /> handle on the left to reorder videos.
        The order shown here is what subscribers see in the Library.
        {saving && <span className="ml-2 text-primary">Saving…</span>}
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {items.map((video) => (
              <SortableVideoRow
                key={video.id}
                video={video}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
                onSendToTop={onSendToTop}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default LibraryVideosSortable;
